<template>
  <div>
    <h5 v-if="showTitle" class="mb-4">{{ title }}</h5>

    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary mb-3"></div>
      <p class="text-muted">Loading rankings...</p>
    </div>

    <div v-else-if="data.length === 0" class="text-center py-5">
      <img :src="'./assets/images/no-results.png'" alt="No results" class="mb-3" style="max-width: 150px;">
      <p class="text-muted">No data available for the selected criteria</p>
    </div>

    <div v-else class="table-responsive">
      <table class="table table-hover mb-0">
        <thead class="table-light">
        <tr>
          <th v-if="columns.includes('rank')" style="width: 60px;">
            <i class="bx bx-medal"></i> Rank
          </th>
          <th v-if="columns.includes('collector')">
            <i class="bx bx-user"></i> Collector Name
          </th>
          <th v-if="columns.includes('subcounty')">
            <i class="bx bx-map"></i> Sub County
          </th>
          <th v-if="columns.includes('transactions')" class="text-end">
            <i class="bx bx-receipt"></i> Transactions
          </th>
          <th v-if="columns.includes('totalAmount')" class="text-end">
            <i class="bx bx-money"></i> Total Amount
          </th>
          <th v-if="columns.includes('avgTransaction')" class="text-end">
            <i class="bx bx-calculator"></i> Avg Transaction
          </th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(item, index) in data" :key="index" class="align-middle">
          <td v-if="columns.includes('rank')">
            <div class="rank-badge" :class="getRankClass(item.rank || index + 1)">
              {{ item.rank || index + 1 }}
            </div>
          </td>
          <td v-if="columns.includes('collector')">
            <div>
              <h6 class="mb-1">{{ item.collectorName || item.invoicedBy || '-' }}</h6>
              <small class="text-muted">ID: {{ item.collectorID || item.invoicedByIdNo || '-' }}</small>
              <br>
              <small class="text-muted" v-if="item.collectorPhone || item.invoicedByPhoneNumber">
                {{ formatPhone(item.collectorPhone || item.invoicedByPhoneNumber) }}
              </small>
            </div>
          </td>
          <td v-if="columns.includes('subcounty')">
            {{ item.subCountyName || '-' }}
          </td>
          <td v-if="columns.includes('transactions')" class="text-end">
            <span class="badge bg-info">{{ numberFormat(item.transactionCount || 0) }}</span>
          </td>
          <td v-if="columns.includes('totalAmount')" class="text-end">
            <strong class="text-success">{{ formatPrice(item.totalCollected || 0) }}</strong>
          </td>
          <td v-if="columns.includes('avgTransaction')" class="text-end">
            <span class="badge bg-secondary">{{ formatPrice(item.averageTransaction || 0) }}</span>
          </td>
        </tr>
        </tbody>
        <tfoot class="table-light" v-if="data.length > 0">
        <tr class="fw-bold">
          <td v-if="columns.includes('rank')"></td>
          <td v-if="columns.includes('collector')">TOTALS</td>
          <td v-if="columns.includes('subcounty')"></td>
          <td v-if="columns.includes('transactions')" class="text-end">
            {{ numberFormat(data.reduce((sum, item) => sum + (item.transactionCount || 0), 0)) }}
          </td>
          <td v-if="columns.includes('totalAmount')" class="text-end">
            {{ formatPrice(data.reduce((sum, item) => sum + parseFloat(item.totalCollected || 0), 0)) }}
          </td>
          <td v-if="columns.includes('avgTransaction')" class="text-end">
            {{ formatPrice(getAverageOfAverages(data)) }}
          </td>
        </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RankingsTable',
  props: {
    data: {
      type: Array,
      required: true
    },
    columns: {
      type: Array,
      default: () => ['rank', 'collector', 'totalAmount', 'transactions', 'avgTransaction']
    },
    title: {
      type: String,
      default: 'Rankings'
    },
    loading: {
      type: Boolean,
      default: false
    },
    showTitle: {
      type: Boolean,
      default: true
    },
    sortBy: {
      type: String,
      default: null
    }
  },
  methods: {
    getRankClass(rank) {
      if (rank === 1) return 'rank-1 gold';
      if (rank === 2) return 'rank-2 silver';
      if (rank === 3) return 'rank-3 bronze';
      return 'rank-default';
    },

    formatPrice(value) {
      const num = parseFloat(value) || 0;
      return new Intl.NumberFormat('en-KE', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 2
      }).format(num);
    },

    numberFormat(num) {
      if (!num) return '0';
      return new Intl.NumberFormat("en-GB").format(num);
    },

    formatPhone(phone) {
      if (!phone) return '-';
      const stringPhone = String(phone);
      if (stringPhone.length > 20) {
        return 'XXX-XXX-' + stringPhone.substr(-4);
      }
      if (stringPhone.startsWith('254')) {
        return '+' + stringPhone;
      }
      return stringPhone;
    },

    getAverageOfAverages(items) {
      if (items.length === 0) return 0;
      const sum = items.reduce((acc, item) => acc + parseFloat(item.averageTransaction || 0), 0);
      return sum / items.length;
    }
  }
}
</script>

<style scoped>
.rank-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  font-weight: 700;
  font-size: 1rem;
  color: white;
}

.rank-badge.gold {
  background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
  box-shadow: 0 2px 8px rgba(255, 215, 0, 0.4);
}

.rank-badge.silver {
  background: linear-gradient(135deg, #C0C0C0 0%, #A9A9A9 100%);
  box-shadow: 0 2px 8px rgba(192, 192, 192, 0.4);
}

.rank-badge.bronze {
  background: linear-gradient(135deg, #CD7F32 0%, #B87333 100%);
  box-shadow: 0 2px 8px rgba(205, 127, 50, 0.4);
}

.rank-badge.rank-default {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.4);
}

.badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.5rem 0.75rem;
}

table thead {
  background-color: #f8f9fa;
}

table tbody tr:hover {
  background-color: #f8f9fa;
}

table tfoot {
  border-top: 2px solid #dee2e6;
}

h6 {
  color: #5a5c69;
  margin-bottom: 0.25rem;
}

.text-success {
  color: #28a745 !important;
}

.text-muted {
  font-size: 0.85rem;
}
</style>